/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import { Notify } from "notiflix/build/notiflix-notify-aio";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useQuickPriceUpdateMutation } from "services/products";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiDialogContent-root": {
        padding: theme.spacing(2),
    },
    "& .MuiDialogActions-root": {
        padding: theme.spacing(1),
    },
}));

const QuickPriceUpdateDialog = ({ productData }) => {
    const [open, setOpen] = useState(false);
    const [price, setPrice] = useState(productData.price || 0);
    const [retailPrice, setRetailPrice] = useState(productData.retail_price || 0);

    const [quickPriceUpdate, { isLoading }] = useQuickPriceUpdateMutation();

    useEffect(() => {
        setPrice(productData.price);
        setRetailPrice(productData.retail_price);
    }, [productData]);

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleSave = async () => {
        try {
            const response = await quickPriceUpdate({
                id: productData.id,
                countryId: productData.country_id,
                data: {
                    price: parseFloat(price),
                    retailPrice: parseFloat(retailPrice),
                },
            });

            if (response.data?.status === "success") {
                Notify.success("Price updated successfully!");
                handleClose();
            } else {
                Notify.failure("Failed to update price!");
            }
        } catch (error) {
            console.error("Error updating price:", error);
            Notify.failure("Something went wrong!");
        }
    };

    return (
        <>
            <Tooltip title="Quick Price Update" placement="top">
                <IconButton
                    aria-label="quick-price-update"
                    size="small"
                    sx={{ ml: 1 }}
                    onClick={handleClickOpen}
                >
                    <AttachMoneyIcon color="success" fontSize="inherit" />
                </IconButton>
            </Tooltip>
            <BootstrapDialog
                onClose={handleClose}
                aria-labelledby="quick-price-dialog-title"
                open={open}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ m: 0, p: 2 }} id="quick-price-dialog-title">
                    Quick Price Update
                </DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{
                        position: "absolute",
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
                <DialogContent dividers>
                    <MDTypography variant="button" fontWeight="bold" sx={{ mb: 2, display: "block" }}>
                        {productData.name}
                    </MDTypography>
                    <MDBox mb={2}>
                        <TextField
                            label="Price"
                            type="number"
                            fullWidth
                            value={price}
                            onChange={(e) => setPrice(e.target.value)}
                            variant="outlined"
                        />
                    </MDBox>
                    <MDBox mb={2}>
                        <TextField
                            label="Retail Price"
                            type="number"
                            fullWidth
                            value={retailPrice}
                            onChange={(e) => setRetailPrice(e.target.value)}
                            variant="outlined"
                        />
                    </MDBox>
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={handleClose} color="error" variant="text">
                        Cancel
                    </MDButton>
                    <MDButton onClick={handleSave} color="info" variant="gradient" disabled={isLoading}>
                        {isLoading ? "Saving..." : "Save Changes"}
                    </MDButton>
                </DialogActions>
            </BootstrapDialog>
        </>
    );
};

export default QuickPriceUpdateDialog;
